<template>
  <Main>
    <v-container fluid style="background:#F8F9FB;min-height:100%;padding:24px;">

      <!-- ── PAGE HEADER ── -->
      <div class="d-flex justify-space-between align-center mb-5">
        <div>
          <div style="font-size:24px;font-weight:700;line-height:34px;color:#191B1D;">Overview Call Realization</div>
          <div style="font-size:14px;color:#959697;line-height:22px;">Monitor call plan realization progress per salesman</div>
        </div>
        <v-btn outlined color="#00B507" style="font-weight:700;border-radius:4px;" @click="downloadCSV()">
          <v-icon left small>mdi-file-excel-outline</v-icon>
          EXPORT EXCEL
        </v-btn>
      </div>

      <!-- ── FILTER + PROGRESS CARD ── -->
      <v-card class="mb-4" outlined style="border-color:#D2DEEB;border-radius:6px;">

        <!-- Filter row -->
        <div class="pa-4" style="border-bottom:1px solid #D2DEEB;">
          <div class="d-flex align-center flex-wrap" style="gap:16px;">
            <!-- Area -->
            <div style="flex:1;min-width:140px;">
              <v-select
                v-model="BranchCode"
                :items="BranchList"
                item-value="BRANCH_CODE"
                item-text="BRANCH_CODE"
                label="Area"
                dense outlined hide-details
                color="red darken-1"
                @change="getPositionCode()"
              ></v-select>
            </div>
            <!-- Position Code -->
            <div style="flex:1;min-width:160px;">
              <v-select
                v-model="PositionCode"
                :items="PositionCodeList"
                item-value="ROLE_POSITION_CODE"
                item-text="ROLE_POSITION_CODE"
                label="Position Code"
                dense outlined hide-details
                color="red darken-1"
              ></v-select>
            </div>
            <!-- Month picker -->
            <div style="flex:1;min-width:140px;position:relative;">
              <v-menu v-model="showMonthPicker" :close-on-content-click="false" offset-y min-width="290px">
                <template v-slot:activator="{ on }">
                  <div
                    v-on="on"
                    style="border:1px solid rgba(0,0,0,0.38);border-radius:4px;padding:8px 12px;cursor:pointer;display:flex;justify-content:space-between;align-items:center;position:relative;"
                  >
                    <span style="font-size:14px;color:#191B1D;">{{ selectedMonthLabel }}</span>
                    <v-icon small color="#959697">mdi-chevron-down</v-icon>
                    <span style="position:absolute;top:-9px;left:8px;background:white;padding:0 4px;font-size:12px;color:#959697;">Month</span>
                  </div>
                </template>
                <v-date-picker
                  v-model="selectedMonth"
                  type="month"
                  color="red darken-1"
                  no-title
                  @input="showMonthPicker = false"
                ></v-date-picker>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- A/T Call Progress bar (Figma style) -->
        <div style="position:relative;height:22px;">
          <div v-show="dtItems.length === 0" style="position:absolute;inset:0;background:#F2F5F9;display:flex;align-items:center;padding-left:16px;">
            <span style="font-size:12px;font-weight:700;color:#D2DEEB;">Call Progress —</span>
          </div>
          <div v-show="dtItems.length > 0" style="position:absolute;inset:0;background:#FCF4ED;display:flex;align-items:center;padding-left:16px;">
            <span style="font-size:12px;font-weight:700;color:#191B1D;">
              Call Progress {{ summaryStats.overallPercentage.toFixed(2) }}%
            </span>
          </div>
          <div
            v-show="dtItems.length > 0"
            :style="{
              position:'absolute', top:0, left:0,
              height:'100%',
              width: Math.min(100, summaryStats.overallPercentage) + '%',
              background: summaryStats.overallPercentage >= 100 ? '#00B507' : '#E59348',
              display:'flex', alignItems:'center',
              paddingLeft:'16px', overflow:'hidden',
              transition: 'width 0.5s ease, background 0.3s ease'
            }"
          >
            <span style="font-size:12px;font-weight:700;color:white;white-space:nowrap;">
              Call Progress {{ summaryStats.overallPercentage.toFixed(2) }}%
            </span>
          </div>
        </div>

        <!-- Stats row: ACTUAL | TARGET | DIFF -->
        <div class="d-flex" style="border-top:1px solid #D2DEEB;">
          <!-- Actual -->
          <div class="d-flex align-center pa-4" style="flex:1;border-right:1px solid #D2DEEB;">
            <div class="d-flex align-center justify-center" style="background:#E9EEF5;border-radius:50%;width:48px;height:48px;min-width:48px;">
              <v-icon color="#E53935">mdi-checkbox-marked-circle-outline</v-icon>
            </div>
            <div class="ml-3">
              <div style="font-size:14px;color:#959697;line-height:22px;">ACTUAL (REALIZATION)</div>
              <div v-show="dtItems.length > 0" style="font-size:16px;font-weight:700;color:#191B1D;line-height:26px;">{{ summaryStats.totalVisited }}</div>
              <div v-show="dtItems.length === 0" style="height:26px;width:40px;background:#E9EEF5;border-radius:4px;"></div>
            </div>
          </div>
          <!-- Target -->
          <div class="d-flex align-center pa-4" style="flex:1;border-right:1px solid #D2DEEB;">
            <div class="d-flex align-center justify-center" style="background:#E9EEF5;border-radius:50%;width:48px;height:48px;min-width:48px;">
              <v-icon color="#E53935">mdi-bullseye</v-icon>
            </div>
            <div class="ml-3">
              <div style="font-size:14px;color:#959697;line-height:22px;">TARGET</div>
              <div v-show="dtItems.length > 0" style="font-size:16px;font-weight:700;color:#191B1D;line-height:26px;">{{ summaryStats.totalPlanned }}</div>
              <div v-show="dtItems.length === 0" style="height:26px;width:40px;background:#E9EEF5;border-radius:4px;"></div>
            </div>
          </div>
          <!-- Diff -->
          <div class="d-flex align-center pa-4" style="flex:1;">
            <div class="d-flex align-center justify-center" style="background:#E9EEF5;border-radius:50%;width:48px;height:48px;min-width:48px;">
              <v-icon color="#E53935">mdi-timer-sand</v-icon>
            </div>
            <div class="ml-3">
              <div style="font-size:14px;color:#959697;line-height:22px;">DIFF (PROGRESS)</div>
              <div v-show="dtItems.length > 0" style="font-size:16px;font-weight:700;color:#191B1D;line-height:26px;">{{ Math.max(0, summaryStats.totalPlanned - summaryStats.totalVisited) }}</div>
              <div v-show="dtItems.length === 0" style="height:26px;width:40px;background:#E9EEF5;border-radius:4px;"></div>
            </div>
          </div>
        </div>
      </v-card>

      <!-- ── DATE STRIP CARD ── -->
      <v-card class="mb-4 date-strip-card" outlined style="border-color:#D2DEEB;border-radius:6px;">
        <div
          v-if="selectedDateRangeLabel"
          class="px-4 pt-2 caption grey--text"
        >
          {{ selectedDateRangeLabel }} — click a day to view that date only
        </div>
        <div class="date-strip-scroll" style="overflow-x:auto;padding:12px 16px 8px;">
          <div class="date-strip-row" style="display:flex;gap:4px;min-width:max-content;">
            <div
              v-for="day in daysInMonth"
              :key="day.day"
              :data-day="day.day"
              class="date-strip-cell"
              :class="getDayCellClass(day.day)"
              @mousedown.prevent="onDayPointerDown(day.day)"
              @mouseenter="onDayPointerEnter(day.day)"
              @touchstart.prevent="onDayPointerDown(day.day)"
            >
              <span class="date-strip-cell__dow">{{ day.dowAbbr }}</span>
              <span class="date-strip-cell__num">{{ day.day }}</span>
            </div>
          </div>
        </div>
      </v-card>

      <!-- ── RESULTS CARD ── -->
      <v-card outlined style="border-color:#D2DEEB;border-radius:6px;">

        <!-- Status filter pills + search -->
        <div
          class="d-flex align-center justify-space-between px-4 py-3"
          style="border-bottom:1px dashed #D2DEEB;flex-wrap:wrap;gap:8px;"
        >
          <!-- Filter pills -->
          <div class="d-flex align-center flex-wrap ov-pill-group">
            <button
              @click="statusFilter = 'all'"
              class="ov-pill"
              :class="{ 'ov-pill--active': statusFilter === 'all' }"
            >
              <span class="ov-pill-label">All</span>
              <span class="ov-pill-badge">{{ dtItems.length }}</span>
            </button>
            <button
              @click="statusFilter = 'match'"
              class="ov-pill"
              :class="{ 'ov-pill--active': statusFilter === 'match' }"
            >
              <span class="ov-pill-label">Match</span>
              <span class="ov-pill-badge">{{ summaryStats.matchCount }}</span>
            </button>
            <button
              @click="statusFilter = 'not-match'"
              class="ov-pill"
              :class="{ 'ov-pill--active': statusFilter === 'not-match' }"
            >
              <span class="ov-pill-label">Not Match</span>
              <span class="ov-pill-badge">{{ summaryStats.notMatchCount }}</span>
            </button>
          </div>

          <!-- Search input -->
          <div class="ov-search-wrap">
            <v-icon small color="#959697" class="mr-1">mdi-magnify</v-icon>
            <input
              v-model="search"
              placeholder="Search..."
              class="ov-search-input"
            />
          </div>
        </div>

        <!-- Tab bar -->
        <div class="ov-tab-bar">
          <button
            v-for="(tab, idx) in tabItems"
            :key="tab"
            type="button"
            class="ov-tab-btn"
            :class="{ 'ov-tab-btn--active': activeTab === idx }"
            @click="activeTab = idx"
          >
            {{ tab }}
          </button>
        </div>

        <v-tabs-items v-model="activeTab">

          <!-- ── TAB 0: OVERVIEW ── -->
          <v-tab-item>
            <div v-if="activeTab === 0" class="realization-table-wrap">
            <v-data-table
              class="ov-overview-table"
              :headers="dtHeaders"
              :items="filteredItems"
              :loading="dtLoading"
              :items-per-page.sync="tableItemsPerPage"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
              fixed-header
              height="600px"
              loading-text="Fetching Data..."
            >
              <template v-slot:item.COORDINATE_STATUS="{ item }">
                <v-chip
                  v-if="item.COORDINATE_STATUS && item.COORDINATE_STATUS !== '-'"
                  :color="item.COORDINATE_STATUS === 'Match' ? 'green' : 'red'"
                  dark small
                >{{ item.COORDINATE_STATUS }}</v-chip>
                <span v-else>-</span>
              </template>
              <template v-slot:item.direction="{ item }">
                <v-btn
                  v-if="item.REALIZATION_COORDINATE !== '-' && item.OUTLET_COORDINATE !== '-'"
                  icon small color="primary"
                  @click="openMapDirections(item.REALIZATION_COORDINATE, item.OUTLET_COORDINATE)"
                  title="Open directions in Google Maps"
                >
                  <v-icon small>mdi-directions</v-icon>
                </v-btn>
                <span v-else>-</span>
              </template>
              <template v-slot:item.edit="{ item }">
                <v-icon small class="mr-2" @click="previewItem(item)">mdi-eye</v-icon>
              </template>
            </v-data-table>
            </div>
          </v-tab-item>

          <!-- ── TAB 1: COORDINATE (split: list + map) ── -->
          <v-tab-item>
            <div v-if="activeTab === 1">
            <div
              v-if="shouldShowRouting"
              class="px-4 py-2 caption d-flex align-center"
              style="background:#EFF6FF;border-bottom:1px solid #D2DEEB;color:#1565C0;gap:6px;"
            >
              <v-icon small color="#1565C0">mdi-routes</v-icon>
              Route path active — showing visit sequence for <strong class="ml-1">{{ PositionCode }}</strong>
            </div>
            <div class="d-flex" style="height:600px;">
              <!-- Left: realization list -->
              <div style="width:360px;min-width:280px;overflow-y:auto;border-right:1px solid #e0e0e0;background:#F2F5F9;">
                <!-- Realization cards -->
                <div v-if="coordinateItems.length === 0" class="pa-6 text-center grey--text">
                  <v-icon large color="grey lighten-1">mdi-map-marker-off</v-icon>
                  <div class="mt-2 caption">No realizations match the current filter</div>
                </div>

                <div
                  v-else-if="!shouldShowRouting && viewportCoordinateItems.length === 0"
                  class="pa-6 text-center grey--text"
                >
                  <v-icon large color="grey lighten-1">mdi-map-search</v-icon>
                  <div class="mt-2 caption">Pan or zoom the map to see realizations in this area</div>
                </div>

                <div
                  v-if="!shouldShowRouting && viewportCoordinateItems.length > MAX_COORDINATE_LIST"
                  class="px-3 py-2 caption orange--text text--darken-2"
                  style="background:#FFF8E1;border-bottom:1px solid #e0e0e0;"
                >
                  Showing first {{ MAX_COORDINATE_LIST }} of {{ viewportCoordinateItems.length }} in current map view.
                </div>

                <div
                  v-for="item in coordinateListDisplay"
                  :key="`${item.POSITION_CODE}-${item.TANGGAL_CALL_PLAN}-${item._seqOrder}-${item._idx}`"
                  class="realization-card"
                  :class="{ 'realization-card--focused': focusedCoordinateKey === item._mapKey }"
                  style="background:white;border-bottom:1px solid #e0e0e0;position:relative;"
                  @click="focusCoordinateOnMap(item)"
                >
                    <!-- Status badge top-right -->
                    <div style="position:absolute;top:0;right:0;z-index:1;">
                      <span
                        class="caption font-weight-bold px-2 py-1"
                        :style="{
                          background: item.COORDINATE_STATUS === 'Match' ? '#E6F8E6' : '#FCE6E7',
                          color: item.COORDINATE_STATUS === 'Match' ? '#00B507' : '#E30513',
                          borderBottomLeftRadius: '8px',
                          display: 'inline-block',
                          fontSize: '11px'
                        }"
                      >{{ item.COORDINATE_STATUS }}</span>
                    </div>

                    <!-- Header: employee + direction -->
                    <div class="px-3 pt-3 pb-1">
                      <div class="d-flex align-center caption grey--text">
                        <span
                          :style="{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: item._pinColor || '#0060AC',
                            color: '#fff',
                            fontSize: '10px',
                            fontWeight: '700',
                            marginRight: '6px',
                            flexShrink: '0',
                          }"
                        >{{ item._seqOrder }}</span>
                        {{ item.POSITION_CODE }}
                      </div>
                      <div class="d-flex justify-space-between align-center">
                        <span class="font-weight-bold" style="color:#22335C;font-size:13px;">{{ item.EMPLOYEE_NAME }}</span>
                        <v-btn
                          v-if="item.REALIZATION_COORDINATE !== '-' && item.OUTLET_COORDINATE !== '-'"
                          x-small text color="primary"
                          @click.stop="openMapDirections(item.REALIZATION_COORDINATE, item.OUTLET_COORDINATE)"
                        >
                          <v-icon x-small class="mr-1">mdi-directions</v-icon>
                          <span style="font-size:10px;font-weight:bold;">DIRECTION</span>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Details -->
                    <div class="px-3 pb-3" style="color:#7A859D;font-size:12px;">
                      <div class="d-flex align-center mb-1">
                        <v-icon x-small class="mr-2" color="grey">mdi-hospital-building</v-icon>
                        {{ item.NAMA_RELASI || '-' }}
                      </div>
                      <div class="d-flex align-center mb-1">
                        <v-icon x-small class="mr-2" color="grey">mdi-clock-outline</v-icon>
                        {{ item.CLOCK_IN || '-' }} – {{ item.CLOCK_OUT || '-' }}
                        <span v-if="item.DURATION" class="ml-2">({{ item.DURATION }})</span>
                      </div>
                      <div class="d-flex align-center">
                        <v-icon x-small class="mr-2" color="grey">mdi-calendar</v-icon>
                        {{ item.TANGGAL_CALL_PLAN || '-' }}
                      </div>
                    </div>
                </div>
              </div>

              <!-- Right: map -->
              <div class="flex-grow-1 realization-map-wrap">
                <div
                  ref="leafletMap"
                  class="realization-map"
                  :class="{ 'realization-map--loading': isLoading }"
                ></div>
              </div>
            </div>
            </div>
          </v-tab-item>

          <!-- ── TAB 2: PROGRESS (employee cards) ── -->
          <v-tab-item>
            <div v-if="activeTab === 2" class="pa-3">
              <!-- Sort controls -->
              <div class="d-flex align-center mb-3 flex-wrap" style="gap:8px;">
                <span class="caption grey--text">Sort by:</span>
                <v-btn
                  small
                  :outlined="progressSortBy !== 'employeeName'"
                  :color="progressSortBy === 'employeeName' ? 'primary' : undefined"
                  @click="setProgressSort('employeeName')"
                >
                  Name
                  <v-icon x-small class="ml-1" v-if="progressSortBy === 'employeeName'">
                    {{ progressSortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                  </v-icon>
                </v-btn>
                <v-btn
                  small
                  :outlined="progressSortBy !== 'percentage'"
                  :color="progressSortBy === 'percentage' ? 'primary' : undefined"
                  @click="setProgressSort('percentage')"
                >
                  Percentage
                  <v-icon x-small class="ml-1" v-if="progressSortBy === 'percentage'">
                    {{ progressSortDir === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                  </v-icon>
                </v-btn>
                <span class="caption grey--text ml-2">VACANT positions always listed last</span>
              </div>

              <!-- Search hint -->
              <div v-if="search" class="mb-3 px-3 py-2 caption grey--text" style="background:#F8F9FA;border:1px solid #e0e0e0;border-radius:6px;">
                Showing results for "{{ search }}" — {{ sortedGroupedEmployees.length }} employee(s)
              </div>

              <div v-if="sortedGroupedEmployees.length === 0" class="py-12 text-center grey--text">
                <v-icon large color="grey lighten-1">mdi-account-off</v-icon>
                <div class="mt-2">No data matches the current filter</div>
              </div>

              <!-- Employee Cards -->
              <v-card
                v-for="emp in sortedGroupedEmployees"
                :key="emp.positionCode"
                class="mb-3 emp-progress-card"
                outlined
              >
                <!-- Card Header: click to expand -->
                <div class="emp-card-header" @click="toggleCard(emp.positionCode)">
                  <div class="emp-card-top">
                    <div class="emp-card-id">{{ emp.positionCode }}</div>
                    <v-icon color="#22335C" size="20">
                      {{ expandedCards[emp.positionCode] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </div>

                  <div class="emp-card-name">{{ emp.employeeName }}</div>

                  <div class="emp-card-bottom">
                    <div class="emp-card-badges">
                      <div class="emp-mini-badge">
                        <v-icon x-small color="#E53935">mdi-format-list-checks</v-icon>
                        <span>{{ emp.visited }}</span>
                      </div>
                      <div class="emp-mini-badge">
                        <v-icon x-small color="#E53935">mdi-bullseye</v-icon>
                        <span>{{ emp.planned }}</span>
                      </div>
                      <div class="emp-mini-badge">
                        <v-icon x-small color="#E53935">mdi-timer-sand</v-icon>
                        <span>{{ emp.diff }}</span>
                      </div>
                    </div>

                    <div class="emp-card-progress">
                      <div class="emp-card-progress__track"></div>
                      <div
                        class="emp-card-progress__fill"
                        :class="{ 'emp-card-progress__fill--complete': emp.percentage >= 100 }"
                        :style="{ width: Math.min(100, emp.percentage) + '%' }"
                      >
                        <span class="emp-card-progress__text">{{ emp.percentage.toFixed(2) }}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Expanded: realization table -->
                <v-expand-transition>
                  <div v-if="expandedCards[emp.positionCode]">
                    <!-- Sort indicator -->
                    <div v-if="detailSortKey" class="px-3 py-2 caption grey--text" style="background:#F8F9FA;border-bottom:1px solid #E9EEF5;">
                      Sorted by <strong>{{ detailSortKey }}</strong> ({{ detailSortDir === 'asc' ? 'A → Z' : 'Z → A' }})
                    </div>

                    <v-simple-table dense>
                      <template v-slot:default>
                        <thead>
                          <tr style="background:#F2F5F9;">
                            <th class="sortable-th" @click="setDetailSort('NAMA_RELASI')">
                              OUTLET
                              <v-icon x-small v-if="detailSortKey === 'NAMA_RELASI'">
                                {{ detailSortDir === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending' }}
                              </v-icon>
                            </th>
                            <th class="sortable-th" @click="setDetailSort('TANGGAL_CALL_PLAN')">
                              DATE PLAN
                              <v-icon x-small v-if="detailSortKey === 'TANGGAL_CALL_PLAN'">
                                {{ detailSortDir === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending' }}
                              </v-icon>
                            </th>
                            <th class="sortable-th" @click="setDetailSort('TYPE_PLAN')">TYPE</th>
                            <th class="sortable-th" @click="setDetailSort('STATUS_VISIT')">VISIT</th>
                            <th class="sortable-th" @click="setDetailSort('CLOCK_IN')">
                              CLOCK IN
                              <v-icon x-small v-if="detailSortKey === 'CLOCK_IN'">
                                {{ detailSortDir === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending' }}
                              </v-icon>
                            </th>
                            <th class="sortable-th" @click="setDetailSort('DURATION')">DURATION</th>
                            <th class="sortable-th" @click="setDetailSort('COORDINATE_STATUS')">
                              STATUS
                              <v-icon x-small v-if="detailSortKey === 'COORDINATE_STATUS'">
                                {{ detailSortDir === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending' }}
                              </v-icon>
                            </th>
                            <th class="text-center">DIRECTION</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(row, idx) in sortedRealizations(emp.realizations)"
                            :key="idx"
                            style="border-color:#E9EEF5;"
                          >
                            <td>
                              <div style="font-size:13px;color:#191B1D;">{{ row.NAMA_RELASI || '-' }}</div>
                            </td>
                            <td style="font-size:12px;color:#7A859D;white-space:nowrap;">{{ row.TANGGAL_CALL_PLAN || '-' }}</td>
                            <td>
                              <v-chip x-small outlined>{{ row.TYPE_PLAN || '-' }}</v-chip>
                            </td>
                            <td>
                              <v-chip
                                x-small
                                :color="row.STATUS_VISIT === 'VISIT' ? 'blue lighten-4' : 'grey lighten-3'"
                                :text-color="row.STATUS_VISIT === 'VISIT' ? 'blue darken-2' : 'grey darken-1'"
                              >{{ row.STATUS_VISIT || '-' }}</v-chip>
                            </td>
                            <td style="font-size:12px;color:#191B1D;white-space:nowrap;">
                              {{ row.CLOCK_IN || '-' }}
                              <span v-if="row.CLOCK_OUT" class="grey--text"> – {{ row.CLOCK_OUT }}</span>
                            </td>
                            <td style="font-size:12px;color:#7A859D;">{{ row.DURATION || '-' }}</td>
                            <td>
                              <v-chip
                                x-small dark
                                :color="row.COORDINATE_STATUS === 'Match' ? 'green' : row.COORDINATE_STATUS === '-' ? 'grey' : 'red'"
                              >{{ row.COORDINATE_STATUS }}</v-chip>
                            </td>
                            <td class="text-center">
                              <v-btn
                                v-if="row.REALIZATION_COORDINATE !== '-' && row.OUTLET_COORDINATE !== '-'"
                                icon x-small color="blue-grey"
                                @click="openMapDirections(row.REALIZATION_COORDINATE, row.OUTLET_COORDINATE)"
                              >
                                <v-icon small>mdi-map-marker-path</v-icon>
                              </v-btn>
                              <span v-else class="grey--text caption">-</span>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </v-expand-transition>
              </v-card>
            </div>
          </v-tab-item>

        </v-tabs-items>
      </v-card>
    </v-container>

    <!-- SNACKBAR -->
    <v-snackbar v-model="isShowSnackbar" top>{{ snackbarText }}</v-snackbar>
    <!-- LOADING OVERLAY -->
    <v-overlay :value="isLoading" :z-index="2000">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- PREVIEW MODAL -->
    <v-dialog v-model="isShowPreview" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="warning">
          <v-btn icon dark @click="isShowPreview = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Preview Attachment</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click="isShowPreview = false">Close</v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-container fluid></v-container>
      </v-card>
    </v-dialog>
  </Main>
</template>

<style>
.acrylic { background-color: rgb(223, 255, 210); }
.realization-map-wrap {
  position: relative;
  z-index: 1;
  isolation: isolate;
}
.realization-map {
  height: 600px;
  width: 100%;
  min-height: 600px;
}
.realization-map--loading {
  pointer-events: none;
}
.realization-map.leaflet-container,
.realization-map .leaflet-container {
  height: 100%;
  width: 100%;
  z-index: 1 !important;
}
.realization-map .leaflet-pane,
.realization-map .leaflet-top,
.realization-map .leaflet-bottom,
.realization-map .leaflet-control {
  z-index: 1 !important;
}
.realization-map .leaflet-marker-pane {
  z-index: 2 !important;
}
.realization-map .leaflet-popup-pane {
  z-index: 3 !important;
}
.realization-table-wrap .v-data-table {
  width: 100%;
  border: 1px solid #D2DEEB;
  border-radius: 6px;
  overflow: hidden;
}
.realization-table-wrap .v-data-table__wrapper {
  max-height: 600px;
}
.realization-table-wrap .ov-overview-table table {
  border-collapse: separate;
  border-spacing: 0;
}
.realization-table-wrap .ov-overview-table thead.v-data-table-header th {
  background: #F2F5F9 !important;
  color: #191B1D !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  line-height: 18px !important;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-bottom: 1px solid #E9EEF5 !important;
  border-top: none !important;
  padding: 14px 12px !important;
  height: auto !important;
  white-space: nowrap;
}
.realization-table-wrap .ov-overview-table thead.v-data-table-header th.sortable {
  cursor: pointer;
}
.realization-table-wrap .ov-overview-table thead.v-data-table-header th.sortable:hover {
  background: #E9EEF5 !important;
}
.realization-table-wrap .ov-overview-table thead.v-data-table-header th .v-data-table-header__icon {
  display: none !important;
}
.realization-table-wrap .ov-overview-table thead.v-data-table-header th.active .v-data-table-header__icon {
  display: inline-flex !important;
  color: #191B1D !important;
  opacity: 1;
}
.realization-table-wrap .ov-overview-table tbody tr {
  background: #FFFFFF;
}
.realization-table-wrap .ov-overview-table tbody tr:hover {
  background: #F8F9FA !important;
}
.realization-table-wrap .ov-overview-table tbody td {
  background: transparent !important;
  color: #191B1D !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 22px !important;
  border-bottom: 1px solid #E9EEF5 !important;
  padding: 14px 12px !important;
  height: auto !important;
  vertical-align: middle;
}
.realization-table-wrap .ov-overview-table .v-data-footer {
  border-top: 1px dashed #D2DEEB;
  background: #FFFFFF;
  color: #959697;
  font-size: 14px;
}
.realization-card:hover { background: #F8F9FA !important; }
.realization-card {
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.realization-card--focused {
  background: #E8F0FE !important;
  border-left: 3px solid #0060AC !important;
}
.sortable-th {
  cursor: pointer;
  font-size: 11px !important;
  font-weight: 700 !important;
  color: #191B1D !important;
  user-select: none;
}
.sortable-th:hover { background: #E9EEF5; }
.marker-cluster-small,
.marker-cluster-medium,
.marker-cluster-large {
  background-color: rgba(0, 96, 172, 0.25);
}
.marker-cluster-small div,
.marker-cluster-medium div,
.marker-cluster-large div {
  background-color: rgba(0, 96, 172, 0.85);
  color: #fff;
  font-weight: 700;
}

/* ── Filter pills ── */
.ov-pill-group {
  gap: 6px;
}
.ov-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 100px;
  border: 1px solid #E53935;
  background: #FFFFFF;
  cursor: pointer;
  transition: background 0.15s;
  line-height: 22px;
}
.ov-pill--active {
  background: rgba(229, 57, 53, 0.1);
}
.ov-pill-label {
  font-size: 14px;
  color: #E53935;
  font-weight: 400;
}
.ov-pill--active .ov-pill-label {
  font-weight: 700;
}
.ov-pill-badge {
  background: #E53935;
  color: #FFFFFF;
  border-radius: 100px;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  min-width: 20px;
  text-align: center;
}

/* ── Date strip ── */
.date-strip-card {
  user-select: none;
  -webkit-user-select: none;
}
.date-strip-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  width: 52px;
  border-radius: 4px;
  cursor: pointer;
  background: #F2F5F9;
  border: 1px solid #D2DEEB;
  transition: background 0.15s, border 0.15s, box-shadow 0.15s;
  touch-action: none;
}
.date-strip-cell__dow {
  font-size: 12px;
  line-height: 18px;
  color: #959697;
}
.date-strip-cell__num {
  font-size: 14px;
  font-weight: 700;
  line-height: 22px;
  color: #191B1D;
}
.date-strip-cell--selected {
  background: #E53935;
  border-color: #E53935;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.12);
}
.date-strip-cell--selected .date-strip-cell__dow {
  color: rgba(255, 255, 255, 0.85);
}
.date-strip-cell--selected .date-strip-cell__num {
  color: #FFFFFF;
}
.date-strip-cell--range-middle {
  background: #F9D0CF;
  border-color: #E53935;
}
.date-strip-cell--range-middle .date-strip-cell__dow {
  color: #C62828;
}
.date-strip-cell--range-middle .date-strip-cell__num {
  color: #B71C1C;
}

/* ── Tab bar ── */
.ov-tab-bar {
  display: flex;
  align-items: stretch;
  background: #E9EEF5;
}
.ov-tab-btn {
  flex: 1;
  padding: 10px 24px;
  border: none;
  background: #FFFFFF;
  border-bottom: 3px solid transparent;
  font-size: 14px;
  font-weight: 700;
  color: #959697;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  line-height: 22px;
}
.ov-tab-btn--active {
  color: #E53935;
  border-bottom: 3px solid #E53935;
}

/* ── Progress employee card ── */
.emp-progress-card {
  border-color: #D2DEEB !important;
  border-radius: 12px !important;
  overflow: hidden;
}
.emp-card-header {
  cursor: pointer;
  padding: 16px;
}
.emp-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}
.emp-card-id {
  font-size: 14px;
  color: #959697;
  line-height: 22px;
}
.emp-card-name {
  font-size: 14px;
  font-weight: 700;
  color: #22335C;
  line-height: 22px;
  margin-bottom: 12px;
}
.emp-card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.emp-card-badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 50%;
  min-width: 0;
}

.emp-mini-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #E9EEF5;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  color: #22335C;
  line-height: 22px;
}

.emp-card-progress {
  position: relative;
  width: 100%;
  min-width: 0;
  height: 22px;
  border-radius: 11px;
  overflow: hidden;
  flex-shrink: 0;
  max-width: 50%;
}
.emp-card-progress__track {
  position: absolute;
  inset: 0;
  background: #D2D7DD;
  border-radius: 11px;
}
.emp-card-progress__fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #E59348;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  transition: width 0.4s ease;
  overflow: hidden;
}
.emp-card-progress__fill--complete {
  background: #00B507;
}
.emp-card-progress__text {
  font-size: 11px;
  font-weight: 700;
  color: #FFFFFF;
  white-space: nowrap;
}

/* ── Map pin markers ── */
.map-pin-marker {
  background: transparent;
  border: none;
}
.map-pin-marker__wrap {
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.25));
}

/* ── Search ── */
.ov-search-wrap {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #959697;
  padding: 4px 4px 2px;
  min-width: 240px;
}
.ov-search-input {
  border: none;
  outline: none;
  font-size: 14px;
  color: #191B1D;
  width: 100%;
  background: transparent;
}
.ov-search-input::placeholder { color: #959697; }
</style>

<script>
import Main from "@/Main.vue";
import moment from "moment";
import XLSX from "xlsx";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const MAX_COORDINATE_LIST = 100;
const SEARCH_DEBOUNCE_MS = 300;
const MAP_DEBOUNCE_MS = 400;
const OVERVIEW_FETCH_DEBOUNCE_MS = 300;

function createMapPinIcon(color) {
  return L.divIcon({
    className: "map-pin-marker",
    html: `<div class="map-pin-marker__wrap">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="28" viewBox="0 0 32 44" aria-hidden="true">
        <ellipse cx="16" cy="41" rx="7" ry="2.5" fill="rgba(0,0,0,0.2)"/>
        <path fill="${color}" stroke="#FFFFFF" stroke-width="1.5" d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 26 16 26s16-14.5 16-26C32 7.163 24.837 0 16 0z"/>
        <circle fill="#FFFFFF" cx="16" cy="16" r="4"/>
      </svg>
    </div>`,
    iconSize: [20, 28],
    iconAnchor: [10, 26],
    popupAnchor: [0, -24],
  });
}

function createNumberedMapPinIcon(color, number) {
  const label = number > 99 ? "99+" : String(number);
  const fontSize = label.length > 2 ? "9" : "12";
  return L.divIcon({
    className: "map-pin-marker",
    html: `<div class="map-pin-marker__wrap">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="34" viewBox="0 0 32 44" aria-hidden="true">
        <ellipse cx="16" cy="41" rx="7" ry="2.5" fill="rgba(0,0,0,0.2)"/>
        <path fill="${color}" stroke="#FFFFFF" stroke-width="1.5" d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 26 16 26s16-14.5 16-26C32 7.163 24.837 0 16 0z"/>
        <text x="16" y="21" text-anchor="middle" fill="#FFFFFF" font-size="${fontSize}" font-weight="bold" font-family="sans-serif">${label}</text>
      </svg>
    </div>`,
    iconSize: [24, 34],
    iconAnchor: [12, 32],
    popupAnchor: [0, -30],
  });
}

const MAP_PIN_ICONS = {
  match: createMapPinIcon("#34A853"),
  notMatch: createMapPinIcon("#EA4335"),
};

const ROUTE_COLOR_SOLID = [
  "#0060AC", "#C8102E", "#007A33", "#E57200", "#5B4A8A",
  "#007FA3", "#8B1E3F", "#1565C0", "#6A1B9A", "#AD1457",
  "#D84315", "#00838F", "#283593", "#558B2F", "#F57F17",
  "#4E342E", "#37474F", "#1B5E20", "#0D47A1", "#4A148C",
  "#BF360C", "#006064", "#1A237E", "#33691E", "#E65100",
];

const ROUTE_COLOR_PASTEL = [
  "#5C6BC0", "#26A69A", "#66BB6A", "#FFA726", "#AB47BC",
  "#42A5F5", "#EC407A", "#8D6E63", "#78909C", "#7E57C2",
  "#29B6F6", "#9CCC65", "#FF7043", "#BA68C8", "#4DB6AC",
  "#7986CB", "#AED581", "#FFB74D", "#F06292", "#4FC3F7",
  "#81C784", "#9575CD", "#FF8A65", "#64B5F6", "#A1887F",
];

const ROUTE_COLOR_JEWEL = [
  "#6B3FA0", "#B8860B", "#3D6B4F", "#A0522D", "#2E8B57",
  "#4169E1", "#D2691E", "#708090", "#9C27B0", "#009688",
  "#FF6F00", "#5D4037", "#455A64", "#C2185B", "#7B1FA2",
  "#0288D1", "#689F38", "#EF6C00", "#4527A0", "#00695C",
  "#880E4F", "#827717", "#01579B", "#6D4C41", "#546E7A",
];

const ROUTE_COLOR_TIERS = [ROUTE_COLOR_SOLID, ROUTE_COLOR_PASTEL, ROUTE_COLOR_JEWEL];

function getRoutePaletteColor(index) {
  const tier = index % ROUTE_COLOR_TIERS.length;
  const tierIndex = Math.floor(index / ROUTE_COLOR_TIERS.length);
  const palette = ROUTE_COLOR_TIERS[tier];
  if (tierIndex < palette.length) {
    return palette[tierIndex];
  }
  const hue = Math.round((index * 137.508) % 360);
  const saturation = tier === 1 ? 58 : 72;
  const lightness = tier === 1 ? 50 : 42;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export default {
  components: { Main },
  data() {
    return {
      activeTab: 0,
      tabItems: ["Overview", "Coordinate", "Progress"],
      dtHeaders: [
        { text: "Area Code", value: "AREA_CODE" },
        { text: "Position Code", value: "POSITION_CODE" },
        { text: "Employee Name", value: "EMPLOYEE_NAME" },
        { text: "Supervisor Position Code", value: "SUPERVISOR_POSITION_CODE" },
        { text: "Supervisor Name", value: "SUPERVISOR_NAME" },
        { text: "Nama Relasi", value: "NAMA_RELASI" },
        { text: "Plan/Unplan", value: "TYPE_PLAN" },
        { text: "Tanggal Call Plan", value: "TANGGAL_CALL_PLAN" },
        { text: "Rencana Kunjungan", value: "RENCANA_KUNJUNGAN" },
        { text: "Hasil Kunjungan", value: "HASIL_KUNJUNGAN" },
        { text: "Ada retur", value: "ADA_RETUR" },
        { text: "Ket Retur", value: "KET_RETUR" },
        { text: "Status Visit", value: "STATUS_VISIT" },
        { text: "Status Realisasi", value: "STATUS_CALL_REALISASI" },
        { text: "Clock In", value: "CLOCK_IN" },
        { text: "Clock Out", value: "CLOCK_OUT" },
        { text: "Duration", value: "DURATION" },
        { text: "Tanggal Realisasi Call", value: "TANGGAL_INPUT_CALL_REALISASI" },
        { text: "Tanggal Approve Realisasi Call", value: "TANGGAL_APPROVE_CALL_REALISASI" },
        { text: "Status Koordinat", value: "COORDINATE_STATUS" },
        { text: "Arah", value: "direction", sortable: false },
      ],
      dtItems: [],
      tableItemsPerPage: 25,
      groupedEmployeesCache: [],
      callTargetMap: {},
      summaryStats: {
        matchCount: 0,
        notMatchCount: 0,
        totalVisited: 0,
        totalPlanned: 0,
        overallPercentage: 0,
      },
      search: "",
      searchDebounced: "",
      searchDebounceTimer: null,
      statusFilter: "all",
      dtLoading: false,
      isLoading: false,
      isShowSnackbar: false,
      snackbarText: "",
      isShowData: true,
      selectedMonth: moment().format("YYYY-MM"),
      selectedDays: [moment().date()],
      dateDragAnchor: null,
      isDateDragging: false,
      didDateDrag: false,
      dateDragPreview: [],
      showMonthPicker: false,
      BranchList: [],
      BranchCode: "",
      PositionCodeList: [],
      PositionCode: "ALL",
      isShowPreview: false,
      imageList: [],
      mapInstance: null,
      mapClusterGroup: null,
      mapMarkerByKey: {},
      mapRouteLayers: [],
      focusedCoordinateKey: null,
      mapInitTimer: null,
      mapViewportTimer: null,
      overviewFetchTimer: null,
      viewportCoordinateItems: [],
      expandedCards: {},
      progressSortBy: "employeeName",
      progressSortDir: "asc",
      detailSortKey: null,
      detailSortDir: "asc",
      MAX_COORDINATE_LIST,
    };
  },

  computed: {
    shouldShowRouting() {
      const isSinglePosition = this.PositionCode && this.PositionCode !== "ALL";
      const isSingleDay = this.normalizeSelectedDays(this.selectedDays).length === 1;
      return isSinglePosition && isSingleDay;
    },

    selectedMonthLabel() {
      return moment(this.selectedMonth, "YYYY-MM").format("MMM YYYY");
    },

    displaySelectedDays() {
      if (this.isDateDragging && this.dateDragPreview.length > 0) {
        return this.dateDragPreview;
      }
      return this.normalizeSelectedDays(this.selectedDays);
    },

    selectedDateRange() {
      const days = this.normalizeSelectedDays(this.selectedDays);
      const m = moment(this.selectedMonth, "YYYY-MM");
      const minDay = days[0] || 1;
      const maxDay = days[days.length - 1] || minDay;
      return {
        startDate: m.clone().date(minDay).format("YYYY-MM-DD"),
        endDate: m.clone().date(maxDay).format("YYYY-MM-DD"),
      };
    },

    selectedDateRangeLabel() {
      const { startDate, endDate } = this.selectedDateRange;
      if (startDate === endDate) return "";
      const startLabel = moment(startDate, "YYYY-MM-DD").format("D MMM");
      const endLabel = moment(endDate, "YYYY-MM-DD").format("D MMM");
      return `${startLabel} – ${endLabel}`;
    },

    daysInMonth() {
      const m = moment(this.selectedMonth, "YYYY-MM");
      const total = m.daysInMonth();
      const days = [];
      const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      for (let d = 1; d <= total; d++) {
        const dow = moment(this.selectedMonth + "-" + String(d).padStart(2, "0"), "YYYY-MM-DD").day();
        days.push({ day: d, dowAbbr: DOW[dow] });
      }
      return days;
    },

    filteredItems() {
      const q = (this.searchDebounced || "").toLowerCase().trim();
      const status = this.statusFilter;

      return this.dtItems.filter((item) => {
        if (status === "match" && item.COORDINATE_STATUS !== "Match") return false;
        if (status === "not-match" && item.COORDINATE_STATUS === "Match") return false;
        if (!q) return true;
        return (
          (item.EMPLOYEE_NAME || "").toLowerCase().includes(q) ||
          (item.NAMA_RELASI || "").toLowerCase().includes(q) ||
          (item.POSITION_CODE || "").toLowerCase().includes(q)
        );
      });
    },

    coordinateItems() {
      return this.filteredItems.filter(
        (item) => item.REALIZATION_COORDINATE && item.REALIZATION_COORDINATE !== "-"
      );
    },

    orderedCoordinateItems() {
      const items = this.coordinateItems;
      const parseTime = (t) => (t && t !== "-" ? t : "");

      const routeKeys = [...new Set(items.map((item) => this.getCoordinateRouteKey(item)))].sort();
      const routeColorMap = {};
      routeKeys.forEach((routeKey, idx) => {
        routeColorMap[routeKey] = getRoutePaletteColor(idx);
      });

      // Group by (POSITION_CODE, TANGGAL_CALL_PLAN) — sequence is independent per employee per day
      const groups = {};
      items.forEach((item) => {
        const key = this.getCoordinateRouteKey(item);
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });

      const result = [];
      Object.entries(groups)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .forEach(([, group]) => {
          const sorted = [...group].sort((a, b) => {
            const aIn = parseTime(a.CLOCK_IN);
            const bIn = parseTime(b.CLOCK_IN);
            if (aIn !== bIn) {
              if (!aIn) return 1;
              if (!bIn) return -1;
              return aIn < bIn ? -1 : 1;
            }
            const aOut = parseTime(a.CLOCK_OUT);
            const bOut = parseTime(b.CLOCK_OUT);
            if (aOut !== bOut) {
              if (!aOut) return 1;
              if (!bOut) return -1;
              return aOut < bOut ? -1 : 1;
            }
            return 0;
          });
          sorted.forEach((item, idx) => {
            const routeKey = this.getCoordinateRouteKey(item);
            result.push({
              ...item,
              _seqOrder: idx + 1,
              _pinColor: routeColorMap[routeKey] || getRoutePaletteColor(0),
              _mapKey: this.getCoordinateItemKey(item),
            });
          });
        });

      return result;
    },

    coordinateListDisplay() {
      if (this.shouldShowRouting) {
        return this.orderedCoordinateItems.map((item, idx) => ({
          ...item,
          _idx: idx,
        }));
      }
      return this.viewportCoordinateItems
        .slice(0, MAX_COORDINATE_LIST)
        .map((item, idx) => ({
          ...item,
          _idx: idx,
        }));
    },

    filteredGroupedEmployees() {
      const q = (this.searchDebounced || "").toLowerCase().trim();
      const status = this.statusFilter;

      return this.groupedEmployeesCache
        .map((emp) => {
          let realizations = emp.realizations;

          if (status === "match") {
            realizations = realizations.filter((r) => r.COORDINATE_STATUS === "Match");
          } else if (status === "not-match") {
            realizations = realizations.filter((r) => r.COORDINATE_STATUS !== "Match");
          }

          if (q) {
            realizations = realizations.filter(
              (r) =>
                (emp.employeeName || "").toLowerCase().includes(q) ||
                (r.NAMA_RELASI || "").toLowerCase().includes(q)
            );
          }

          return { ...emp, realizations };
        })
        .filter((emp) => {
          if (!q) return emp.realizations.length > 0;
          return (
            emp.realizations.length > 0 ||
            (emp.employeeName || "").toLowerCase().includes(q)
          );
        });
    },

    sortedGroupedEmployees() {
      const employees = [...this.filteredGroupedEmployees];
      const dir = this.progressSortDir === "asc" ? 1 : -1;

      return employees.sort((a, b) => {
        const aVacant = this.isVacantEmployee(a);
        const bVacant = this.isVacantEmployee(b);
        if (aVacant !== bVacant) {
          return aVacant ? 1 : -1;
        }

        if (this.progressSortBy === "percentage") {
          const diff = a.percentage - b.percentage;
          if (diff === 0) return 0;
          return diff > 0 ? dir : -dir;
        }

        const nameA = (a.employeeName || "").toLowerCase();
        const nameB = (b.employeeName || "").toLowerCase();
        if (nameA < nameB) return -dir;
        if (nameA > nameB) return dir;
        return 0;
      });
    },
  },

  watch: {
    search() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounced = this.search;
      }, SEARCH_DEBOUNCE_MS);
    },
    activeTab(val) {
      if (val === 1) {
        this.scheduleLeafletMapInit();
      }
    },
    filteredItems() {
      if (this.activeTab === 1) {
        this.scheduleLeafletMapInit();
      }
    },
    shouldShowRouting(val) {
      if (this.activeTab === 1 && this.mapInstance) {
        if (val) {
          this.drawRoutes();
        } else {
          this.clearRoutes();
        }
      }
    },
    PositionCode() {
      this.scheduleOverviewFetch();
    },
    selectedMonth() {
      const daysInMonth = moment(this.selectedMonth, "YYYY-MM").daysInMonth();
      const normalized = this.normalizeSelectedDays(this.selectedDays);
      if (normalized.length === 0) {
        const today = moment();
        const isCurrentMonth = today.format("YYYY-MM") === this.selectedMonth;
        this.selectedDays = [isCurrentMonth ? Math.min(today.date(), daysInMonth) : 1];
      } else {
        this.selectedDays = normalized;
      }
    },
    selectedDays: {
      deep: true,
      handler() {
        this.scheduleOverviewFetch();
      },
    },
  },

  mounted() {
    this.getArea();
    window.addEventListener("mouseup", this.onWindowPointerUp);
    window.addEventListener("touchend", this.onWindowPointerUp);
    document.addEventListener("touchmove", this.onDocumentTouchMove, { passive: false });
  },

  beforeDestroy() {
    window.removeEventListener("mouseup", this.onWindowPointerUp);
    window.removeEventListener("touchend", this.onWindowPointerUp);
    document.removeEventListener("touchmove", this.onDocumentTouchMove);
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    if (this.mapInitTimer) {
      clearTimeout(this.mapInitTimer);
      this.mapInitTimer = null;
    }
    if (this.mapViewportTimer) {
      clearTimeout(this.mapViewportTimer);
      this.mapViewportTimer = null;
    }
    if (this.overviewFetchTimer) {
      clearTimeout(this.overviewFetchTimer);
      this.overviewFetchTimer = null;
    }
    this.clearRoutes();
    if (this.mapInstance) {
      this.mapInstance.remove();
      this.mapInstance = null;
    }
  },

  methods: {
    normalizeSelectedDays(days) {
      const maxDayInMonth = moment(this.selectedMonth, "YYYY-MM").daysInMonth();
      const validDays = [...new Set((days || []).filter((day) => day >= 1 && day <= maxDayInMonth))];
      if (validDays.length === 0) return [];

      const start = Math.min(...validDays);
      const end = Math.max(...validDays);
      const range = [];
      for (let day = start; day <= end; day += 1) {
        range.push(day);
      }
      return range;
    },

    getDayRange(fromDay, toDay) {
      const min = Math.min(fromDay, toDay);
      const max = Math.max(fromDay, toDay);
      const days = [];
      for (let day = min; day <= max; day += 1) {
        days.push(day);
      }
      return days;
    },

    isContiguousRange(days) {
      if (days.length <= 1) return true;
      const sorted = [...days].sort((a, b) => a - b);
      for (let i = 1; i < sorted.length; i += 1) {
        if (sorted[i] - sorted[i - 1] !== 1) return false;
      }
      return true;
    },

    getDayCellClass(day) {
      const selected = this.displaySelectedDays;
      if (!selected.includes(day)) return {};

      const sorted = [...selected].sort((a, b) => a - b);
      const isRange = sorted.length > 1 && this.isContiguousRange(sorted);
      const isMiddle = isRange && day > sorted[0] && day < sorted[sorted.length - 1];

      return {
        "date-strip-cell--selected": !isMiddle,
        "date-strip-cell--range-middle": isMiddle,
      };
    },

    onDayPointerDown(day) {
      this.dateDragAnchor = day;
      this.isDateDragging = true;
      this.didDateDrag = false;
      this.dateDragPreview = [day];
    },

    onDayPointerEnter(day) {
      if (!this.isDateDragging || this.dateDragAnchor == null) return;
      if (day !== this.dateDragAnchor) {
        this.didDateDrag = true;
      }
      this.dateDragPreview = this.getDayRange(this.dateDragAnchor, day);
    },

    onDocumentTouchMove(event) {
      if (!this.isDateDragging) return;
      const touch = event.touches[0];
      if (!touch) return;

      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const cell = element && element.closest("[data-day]");
      if (!cell) return;

      const day = parseInt(cell.getAttribute("data-day"), 10);
      if (!Number.isNaN(day)) {
        this.onDayPointerEnter(day);
      }
    },

    onWindowPointerUp() {
      if (!this.isDateDragging || this.dateDragAnchor == null) return;

      const clickedDay = this.dateDragAnchor;
      this.isDateDragging = false;
      this.dateDragAnchor = null;

      if (this.didDateDrag) {
        this.selectedDays = this.normalizeSelectedDays(this.dateDragPreview);
        this.didDateDrag = false;
        this.dateDragPreview = [];
        return;
      }

      this.handleDayClick(clickedDay);
      this.dateDragPreview = [];
    },

    handleDayClick(day) {
      const current = this.normalizeSelectedDays(this.selectedDays);
      const rangeStart = current[0];
      const rangeEnd = current[current.length - 1];
      const isRange = current.length > 1;

      if (isRange && day >= rangeStart && day <= rangeEnd) {
        this.selectedDays = [day];
        return;
      }

      if (!isRange && rangeStart === day) {
        return;
      }

      if (!isRange) {
        this.selectedDays = this.normalizeSelectedDays([rangeStart, day]);
        return;
      }

      this.selectedDays = this.normalizeSelectedDays([rangeStart, rangeEnd, day]);
    },

    scheduleOverviewFetch() {
      if (this.overviewFetchTimer) {
        clearTimeout(this.overviewFetchTimer);
      }
      this.overviewFetchTimer = setTimeout(() => {
        if (this.BranchCode) {
          this.getOverview();
        }
      }, OVERVIEW_FETCH_DEBOUNCE_MS);
    },

    getCallTargetMetrics(positionCode) {
      const target = this.callTargetMap[positionCode];
      if (!target) {
        return { visited: 0, planned: 0 };
      }

      return {
        visited: target.jmlreal || 0,
        planned: target.targetcall > 0 ? target.targetcall : (target.jmlplan || 0),
      };
    },

    buildSummaryStatsFromCallTargets() {
      let totalVisited = 0;
      let totalPlanned = 0;

      Object.keys(this.callTargetMap).forEach((positionCode) => {
        const metrics = this.getCallTargetMetrics(positionCode);
        totalVisited += metrics.visited;
        totalPlanned += metrics.planned;
      });

      return {
        totalVisited,
        totalPlanned,
        overallPercentage: totalPlanned > 0 ? (totalVisited / totalPlanned) * 100 : 0,
      };
    },

    rebuildDerivedData() {
      let matchCount = 0;
      let notMatchCount = 0;
      const groupMap = {};

      this.dtItems.forEach((row) => {
        if (row.COORDINATE_STATUS === "Match") {
          matchCount += 1;
        } else {
          notMatchCount += 1;
        }

        const key = row.POSITION_CODE || row.EMPLOYEE_NAME;
        if (!groupMap[key]) {
          groupMap[key] = {
            positionCode: row.POSITION_CODE,
            employeeName: row.EMPLOYEE_NAME,
            supervisorName: row.SUPERVISOR_NAME,
            supervisorCode: row.SUPERVISOR_POSITION_CODE,
            areaCode: row.AREA_CODE,
            realizations: [],
            visited: 0,
            planned: 0,
          };
        }

        const group = groupMap[key];
        group.realizations.push(row);
        group.planned += 1;
        if (row.STATUS_VISIT === "VISIT") {
          group.visited += 1;
        }
      });

      this.groupedEmployeesCache = Object.values(groupMap).map((group) => {
        const visited = group.visited;
        const planned = group.planned;
        const diff = Math.max(0, planned - visited);
        const percentage = planned > 0 ? (visited / planned) * 100 : 0;

        return {
          ...group,
          visited,
          planned,
          diff,
          percentage,
        };
      });

      const callTargetSummary = this.buildSummaryStatsFromCallTargets();

      this.summaryStats = {
        matchCount,
        notMatchCount,
        totalVisited: callTargetSummary.totalVisited,
        totalPlanned: callTargetSummary.totalPlanned,
        overallPercentage: callTargetSummary.overallPercentage,
      };
    },

    // ── Sorting (Progress tab employee cards) ──
    isVacantEmployee(employee) {
      return (employee.employeeName || "").toUpperCase().includes("VACANT");
    },

    setProgressSort(key) {
      if (this.progressSortBy === key) {
        this.progressSortDir = this.progressSortDir === "asc" ? "desc" : "asc";
      } else {
        this.progressSortBy = key;
        this.progressSortDir = key === "percentage" ? "desc" : "asc";
      }
    },

    // ── Sorting (expanded realization rows) ──
    setDetailSort(key) {
      if (this.detailSortKey === key) {
        this.detailSortDir = this.detailSortDir === "asc" ? "desc" : "asc";
      } else {
        this.detailSortKey = key;
        this.detailSortDir = "asc";
      }
    },
    sortedRealizations(rows) {
      if (!this.detailSortKey) return rows;
      const key = this.detailSortKey;
      const dir = this.detailSortDir === "asc" ? 1 : -1;
      return [...rows].sort((a, b) => {
        const av = (a[key] || "").toString().toLowerCase();
        const bv = (b[key] || "").toString().toLowerCase();
        return av < bv ? -dir : av > bv ? dir : 0;
      });
    },

    // ── Card expand ──
    toggleCard(key) {
      this.$set(this.expandedCards, key, !this.expandedCards[key]);
    },

    // ── Map ──
    scheduleLeafletMapInit() {
      if (this.mapInitTimer) {
        clearTimeout(this.mapInitTimer);
      }
      this.$nextTick(() => {
        this.mapInitTimer = setTimeout(() => {
          this.initLeafletMap();
        }, MAP_DEBOUNCE_MS);
      });
    },

    resetLeafletContainer(container) {
      this.clearRoutes();
      if (this.mapInstance) {
        this.mapInstance.off("moveend", this.onMapViewportChange);
        this.mapInstance.remove();
        this.mapInstance = null;
      }
      this.mapClusterGroup = null;
      this.mapMarkerByKey = {};
      this.focusedCoordinateKey = null;
      this.viewportCoordinateItems = [];
      if (container._leaflet_id) {
        container._leaflet_id = null;
      }
      container.innerHTML = "";
    },

    getCoordinateRouteKey(item) {
      return `${(item.POSITION_CODE || "").trim()}__${(item.TANGGAL_CALL_PLAN || "").trim()}`;
    },

    getCoordinateItemKey(item) {
      return [
        item.POSITION_CODE || "",
        item.TANGGAL_CALL_PLAN || "",
        item.CLOCK_IN || "",
        item.CLOCK_OUT || "",
        item.NAMA_RELASI || "",
        item.REALIZATION_COORDINATE || "",
      ].join("||");
    },

    focusCoordinateOnMap(item) {
      if (!item || !item.REALIZATION_COORDINATE || item.REALIZATION_COORDINATE === "-") return;

      const mapKey = item._mapKey || this.getCoordinateItemKey(item);
      this.focusedCoordinateKey = mapKey;

      const [lat, lng] = item.REALIZATION_COORDINATE.split(",").map(Number);
      if (isNaN(lat) || isNaN(lng) || !this.mapInstance) return;

      const marker = this.mapMarkerByKey[mapKey];
      const openPopup = () => {
        if (marker) marker.openPopup();
      };

      if (marker && this.mapClusterGroup) {
        this.mapClusterGroup.zoomToShowLayer(marker, openPopup);
        return;
      }

      const targetZoom = Math.max(this.mapInstance.getZoom(), 16);
      this.mapInstance.flyTo([lat, lng], targetZoom, { duration: 0.5 });
      this.mapInstance.once("moveend", openPopup);
    },

    onMapViewportChange() {
      this.scheduleViewportListUpdate();
    },

    scheduleViewportListUpdate() {
      if (this.mapViewportTimer) {
        clearTimeout(this.mapViewportTimer);
      }
      this.mapViewportTimer = setTimeout(() => {
        this.updateViewportCoordinateItems();
      }, 200);
    },

    updateViewportCoordinateItems() {
      if (!this.mapInstance) return;

      const bounds = this.mapInstance.getBounds();
      this.viewportCoordinateItems = this.orderedCoordinateItems.filter((item) => {
        const [lat, lng] = item.REALIZATION_COORDINATE.split(",").map(Number);
        if (isNaN(lat) || isNaN(lng)) return false;
        return bounds.contains([lat, lng]);
      });
    },

    buildMarkerPopup(item) {
      const isMatch = item.COORDINATE_STATUS === "Match";
      const statusColor = isMatch ? "#00B507" : "#E30513";
      const statusBg = isMatch ? "#E6F8E6" : "#FCE6E7";
      const pinColor = item._pinColor || "#0060AC";
      const dirLink =
        item.OUTLET_COORDINATE && item.OUTLET_COORDINATE !== "-"
          ? `<br><a href="https://www.google.com/maps/dir/${item.REALIZATION_COORDINATE}/${item.OUTLET_COORDINATE}" target="_blank" style="color:#0060AC;font-weight:bold;">&#8599; Directions</a>`
          : "";

      return `
        <div style="min-width:220px;font-family:sans-serif;font-size:13px;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
            <span style="display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:${pinColor};color:#fff;font-size:10px;font-weight:bold;flex-shrink:0;">${item._seqOrder}</span>
            <span style="display:inline-block;padding:2px 8px;border-radius:4px;background:${statusBg};color:${statusColor};font-weight:bold;font-size:11px;">${item.COORDINATE_STATUS}</span>
          </div>
          <div style="font-weight:bold;color:#22335C;margin-bottom:2px;">${item.EMPLOYEE_NAME || "-"}</div>
          <div style="font-size:11px;color:#959697;margin-bottom:4px;">${item.POSITION_CODE || ""}</div>
          <div style="color:#7A859D;">
            <div>&#127968; ${item.NAMA_RELASI || "-"}</div>
            <div>&#128336; ${item.CLOCK_IN || "-"} – ${item.CLOCK_OUT || "-"}</div>
          </div>
          ${dirLink}
        </div>
      `;
    },

    initLeafletMap() {
      if (this.activeTab !== 1 || !this.$refs.leafletMap) return;

      const container = this.$refs.leafletMap;
      this.resetLeafletContainer(container);

      this.mapInstance = L.map(container, {
        preferCanvas: true,
      }).setView([-6.2088, 106.8456], 10);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(this.mapInstance);

      const bounds = [];

      if (this.shouldShowRouting) {
        // Single position + single day: no clustering, all markers shown directly
        this.orderedCoordinateItems.forEach((item) => {
          const [lat, lng] = item.REALIZATION_COORDINATE.split(",").map(Number);
          if (isNaN(lat) || isNaN(lng)) return;
          const icon = createNumberedMapPinIcon(item._pinColor, item._seqOrder);
          const marker = L.marker([lat, lng], { icon });
          marker.bindPopup(this.buildMarkerPopup(item));
          marker.addTo(this.mapInstance);
          this.mapMarkerByKey[item._mapKey] = marker;
          bounds.push([lat, lng]);
        });
        this.mapClusterGroup = null;
      } else {
        const clusterGroup = L.markerClusterGroup({
          maxClusterRadius: 45,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          disableClusteringAtZoom: 15,
          chunkedLoading: true,
        });
        this.orderedCoordinateItems.forEach((item) => {
          const [lat, lng] = item.REALIZATION_COORDINATE.split(",").map(Number);
          if (isNaN(lat) || isNaN(lng)) return;
          const icon = createNumberedMapPinIcon(item._pinColor, item._seqOrder);
          const marker = L.marker([lat, lng], { icon });
          marker.bindPopup(this.buildMarkerPopup(item));
          clusterGroup.addLayer(marker);
          this.mapMarkerByKey[item._mapKey] = marker;
          bounds.push([lat, lng]);
        });
        this.mapClusterGroup = clusterGroup;
        this.mapInstance.addLayer(clusterGroup);
      }

      this.mapInstance.on("moveend", this.onMapViewportChange);

      this.$nextTick(() => {
        if (!this.mapInstance) return;
        this.mapInstance.invalidateSize(true);
        if (bounds.length > 0) {
          this.mapInstance.fitBounds(bounds, { padding: [30, 30] });
        }
        this.updateViewportCoordinateItems();
        if (this.shouldShowRouting) {
          this.drawRoutes();
        }
      });
    },

    openMapDirections(realizationCoords, outletCoords) {
      const [lat1, lng1] = realizationCoords.split(",").map((c) => c.trim());
      const [lat2, lng2] = outletCoords.split(",").map((c) => c.trim());
      window.open(`https://www.google.com/maps/dir/${lat1},${lng1}/${lat2},${lng2}`, "_blank");
    },

    // ── Route drawing (OSRM, fallback to straight polyline) ──
    async fetchOSRMRoute(waypoints) {
      // OSRM public demo server — free & open source, max 25 waypoints per request
      if (waypoints.length > 25) return null;
      try {
        const coords = waypoints.map((wp) => `${wp.lng},${wp.lat}`).join(";");
        const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        if (!res.ok) return null;
        const json = await res.json();
        if (json.code === "Ok" && json.routes && json.routes[0]) {
          return json.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        }
      } catch {
        // network error — fall through to straight-line fallback
      }
      return null;
    },

    async drawRoutes() {
      this.clearRoutes();
      if (!this.shouldShowRouting || !this.mapInstance) return;

      const items = this.orderedCoordinateItems;
      if (items.length < 2) return;

      // Group items by route key (position + date) — with single position+day there is one group
      const groups = {};
      items.forEach((item) => {
        const key = this.getCoordinateRouteKey(item);
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });

      for (const [, group] of Object.entries(groups)) {
        if (group.length < 2) continue;

        const waypoints = group
          .map((item) => {
            const [lat, lng] = item.REALIZATION_COORDINATE.split(",").map(Number);
            return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
          })
          .filter(Boolean);

        if (waypoints.length < 2) continue;

        const color = group[0]._pinColor || "#0060AC";
        const osrmLatLngs = await this.fetchOSRMRoute(waypoints);

        if (osrmLatLngs && osrmLatLngs.length >= 2) {
          // Solid line — actual road route from OSRM
          const polyline = L.polyline(osrmLatLngs, {
            color,
            weight: 4,
            opacity: 0.85,
          });
          polyline.addTo(this.mapInstance);
          this.mapRouteLayers.push(polyline);
        } else {
          // Dashed straight-line fallback
          const latLngs = waypoints.map((wp) => [wp.lat, wp.lng]);
          const polyline = L.polyline(latLngs, {
            color,
            weight: 3,
            opacity: 0.75,
            dashArray: "8, 6",
          });
          polyline.addTo(this.mapInstance);
          this.mapRouteLayers.push(polyline);
        }
      }
    },

    clearRoutes() {
      this.mapRouteLayers.forEach((layer) => {
        if (this.mapInstance) this.mapInstance.removeLayer(layer);
      });
      this.mapRouteLayers = [];
    },

    downloadCSV() {
      const workBook = XLSX.utils.book_new();
      const workSheet = XLSX.utils.json_to_sheet(this.filteredItems);
      XLSX.utils.book_append_sheet(workBook, workSheet, "Sheet1");
      XLSX.writeFile(workBook, "Overview Call Realization.xlsx");
    },

    previewItem(item) {
      this.isShowPreview = true;
      this.$http
        .post("/file-viewer/detailOverview", { CRCHeaderID: item.CRC_HEADERID }, { headers: { "api-key": this.$apiKey } })
        .then((res) => { this.imageList = res.data.data; })
        .catch((err) => this.showSnackbar(err.toString()));
    },

    getArea() {
      const userData = JSON.parse(localStorage.getItem("userData"));
      this.$http
        .post("/file-viewer/selectArea", { username: userData.USER_NAME }, { headers: { "api-key": this.$apiKey } })
        .then((res) => {
          const branches = (res.data.data || []).filter(
            (branch) =>
              branch.BRANCH_CODE &&
              branch.BRANCH_CODE !== "ALL" &&
              branch.BRANCH_CODE !== "ALL BRANCH"
          );
          this.BranchList = branches;
          if (branches.length > 0) {
            const hasCurrentBranch = branches.some(
              (branch) => branch.BRANCH_CODE === this.BranchCode
            );
            if (!hasCurrentBranch) {
              this.BranchCode = branches[0].BRANCH_CODE;
            }
            this.getPositionCode();
          } else {
            this.BranchCode = "";
            this.PositionCodeList = [];
            this.PositionCode = "";
          }
        })
        .catch((err) => this.showSnackbar(err.toString()));
    },

    getPositionCode() {
      this.$http
        .post("/file-viewer/selectPositionCode", { branchCode: this.BranchCode }, { headers: { "api-key": this.$apiKey } })
        .then((res) => {
          const prevPositionCode = this.PositionCode;
          this.PositionCodeList = res.data.data;
          this.PositionCode = "ALL";
          if (prevPositionCode === "ALL") {
            this.scheduleOverviewFetch();
          }
        })
        .catch((err) => this.showSnackbar(err.toString()));
    },

    getOverview() {
      if (!this.BranchCode) {
        this.showSnackbar("Please select Area first");
        return;
      }

      const { startDate, endDate } = this.selectedDateRange;
      const data = {
        BranchCode: this.BranchCode,
        PositionCode: this.PositionCode === "ALL" ? "" : this.PositionCode,
        StartDate: startDate,
        EndDate: endDate,
      };


      this.isLoading = true;
      this.dtLoading = true;
      this.expandedCards = {};
      this.callTargetMap = {};
      this.$http
        .post("/call-management/OverviewCallRealization", data, { headers: { "api-key": this.$apiKey } })
        .then((res) => {
          const { success, message, data } = res.data;
          this.isLoading = false;
          this.dtLoading = false;
          if (success) {
            this.dtItems = Array.isArray(data) ? data : (data.items || []);
            this.callTargetMap = Array.isArray(data) ? {} : (data.callTargets || {});
            this.rebuildDerivedData();
            if (this.activeTab === 1) {
              this.scheduleLeafletMapInit();
            }
          } else {
            this.showSnackbar(message);
          }
        })
        .catch((err) => {
          this.isLoading = false;
          this.dtLoading = false;
          this.showSnackbar(err.toString());
        });
    },

    showSnackbar(text) {
      this.snackbarText = text;
      this.isShowSnackbar = true;
    },
  },
};
</script>
